import React, { useState, useCallback, useMemo } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { countries } from "./countries";
import { Input } from "../Input";

interface PhoneInputSimpleProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  onPhoneDataChange?: (data: {
    countryCode: string;
    phoneNumber: string;
    fullNumber: string;
  }) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  label?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  hideCountryCode?: boolean;
}

const PhoneInputSimple = React.memo(function PhoneInputSimple({
  value = "",
  onChange,
  onPhoneDataChange,
  placeholder = "Enter phone number",
  disabled = false,
  error,
  label,
  size = "md",
  hideCountryCode = true,
}: PhoneInputSimpleProps) {
  const [inputValue, setInputValue] = useState(value);
  const [countryCode, setCountryCode] = useState("US");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Function to get national number (without country code) for display
  const getNationalNumber = useCallback((phoneNumber: string) => {
    if (!phoneNumber) return "";

    try {
      const parsed = parsePhoneNumberFromString(phoneNumber);
      if (parsed) {
        return parsed.nationalNumber || phoneNumber;
      }
    } catch {
      // If parsing fails, try to remove common country codes manually
      const cleaned = phoneNumber.replace(/^\+\d{1,3}\s?/, "");
      return cleaned;
    }

    return phoneNumber;
  }, []);

  // Function to get display value based on hideCountryCode prop
  const getDisplayValue = useCallback(
    (phoneNumber: string) => {
      if (hideCountryCode) {
        return getNationalNumber(phoneNumber);
      }
      return phoneNumber;
    },
    [hideCountryCode, getNationalNumber],
  );

  // Function to extract phone data and call onPhoneDataChange
  const extractPhoneData = useCallback(
    (phoneNumber: string) => {
      if (!phoneNumber || !onPhoneDataChange) return;

      try {
        const parsed = parsePhoneNumberFromString(phoneNumber);
        if (parsed) {
          onPhoneDataChange({
            countryCode: parsed.countryCallingCode,
            phoneNumber: parsed.nationalNumber,
            fullNumber: phoneNumber,
          });
        } else {
          // Fallback if parsing fails
          onPhoneDataChange({
            countryCode: "",
            phoneNumber: phoneNumber,
            fullNumber: phoneNumber,
          });
        }
      } catch {
        // Fallback if parsing fails
        onPhoneDataChange({
          countryCode: "",
          phoneNumber: phoneNumber,
          fullNumber: phoneNumber,
        });
      }
    },
    [onPhoneDataChange],
  );

  const allCountries = useMemo(() => countries, []);

  // Get current country info
  const currentCountry = useMemo(() => {
    return (
      allCountries.find((country) => country.code === countryCode) ||
      allCountries[0]
    );
  }, [allCountries, countryCode]);

  // Update input value when value prop changes
  React.useEffect(() => {
    if (hideCountryCode) {
      // When hideCountryCode is true, we only update the input if it's empty
      // to avoid overriding what the user is typing
      if (!inputValue) {
        setInputValue(getDisplayValue(value));
      }
    } else {
      // Normal behavior when hideCountryCode is false
      setInputValue(getDisplayValue(value));
    }

    // Try to detect country from phone number
    if (value && value.startsWith("+")) {
      try {
        const parsed = parsePhoneNumberFromString(value);
        if (parsed && parsed.country) {
          setCountryCode(parsed.country);
        }
      } catch {
        // Keep current country if parsing fails
      }
    }
  }, [value, getDisplayValue, hideCountryCode, inputValue]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
        setCountrySearchTerm("");
      }
    };

    if (isCountryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isCountryDropdownOpen]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const numericValue = e.target.value.replace(/\D/g, "");
      // Limit to 15 digits
      const newValue = numericValue.slice(0, 15);

      // If hideCountryCode is true, we always show only the national number in the input
      if (hideCountryCode) {
        setInputValue(newValue);

        // For parsing and sending to parent, we need to add the country code
        if (newValue) {
          try {
            const valueToParse = `+${currentCountry.dialCode}${newValue}`;
            const parsed = parsePhoneNumberFromString(
              valueToParse,
              countryCode as any,
            );
            if (parsed) {
              onChange?.(parsed.number);
              extractPhoneData(parsed.number);
            } else {
              onChange?.(valueToParse);
              extractPhoneData(valueToParse);
            }
          } catch {
            const valueToParse = `+${currentCountry.dialCode}${newValue}`;
            onChange?.(valueToParse);
            extractPhoneData(valueToParse);
          }
        } else {
          onChange?.(undefined);
          extractPhoneData("");
        }
      } else {
        // Normal behavior when hideCountryCode is false
        setInputValue(newValue);

        if (newValue) {
          try {
            const parsed = parsePhoneNumberFromString(
              newValue,
              countryCode as any,
            );
            if (parsed) {
              onChange?.(parsed.number);
              extractPhoneData(parsed.number);
            } else {
              onChange?.(newValue);
              extractPhoneData(newValue);
            }
          } catch {
            onChange?.(newValue);
            extractPhoneData(newValue);
          }
        } else {
          onChange?.(undefined);
          extractPhoneData("");
        }
      }
    },
    [
      onChange,
      countryCode,
      hideCountryCode,
      currentCountry.dialCode,
      extractPhoneData,
    ],
  );

  // Filter countries based on search term
  const filteredCountries = useMemo(() => {
    if (!countrySearchTerm) return allCountries;
    return allCountries.filter(
      (country) =>
        country.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
        country.code.toLowerCase().includes(countrySearchTerm.toLowerCase()),
    );
  }, [allCountries, countrySearchTerm]);

  // Get height classes based on size
  const getHeightClasses = (size: string) => {
    switch (size) {
      case "sm":
        return "h-8";
      case "lg":
        return "h-11";
      default:
        return "h-9";
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="text-foreground mb-2 block text-sm font-normal">
          {label}
        </label>
      )}
      <div
        className={`flex rounded-md border ${error ? "border-red-500" : "border-border"} ${disabled ? "opacity-50" : ""} ${getHeightClasses(size)} focus-within:ring-ring focus-within:border-ring focus-within:ring-2`}
      >
        {/* Country Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
            disabled={disabled}
            className="border-border text-foreground hover:bg-accent/50 flex h-full min-w-[120px] items-center rounded-l-[calc(0.375rem-1px)] border-r px-3 py-2"
          >
            <span className="mr-2">{currentCountry.flag}</span>
            <span className="text-sm">{currentCountry.code}</span>
            <svg
              className="ml-1 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isCountryDropdownOpen && (
            <div className="bg-popover border-border absolute top-full left-0 z-50 mt-1 max-h-60 w-80 overflow-hidden rounded-lg border shadow-lg">
              <div className="border-border border-b p-2">
                <Input
                  type="text"
                  placeholder="Search countries..."
                  value={countrySearchTerm}
                  onChange={(e) => setCountrySearchTerm(e.target.value)}
                  autoFocus
                  className="w-full text-sm"
                  size="sm"
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => {
                      setCountryCode(country.code);
                      setIsCountryDropdownOpen(false);
                      setCountrySearchTerm("");
                      // Re-extract phone data with new country
                      if (inputValue) {
                        const valueToParse = `+${country.dialCode}${inputValue}`;
                        extractPhoneData(valueToParse);
                      }
                    }}
                    className={`hover:bg-accent w-full px-3 py-2 text-left transition-colors ${
                      countryCode === country.code
                        ? "bg-accent text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="mr-2">{country.flag}</span>
                    <span className="text-sm">{country.name}</span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      ({country.code})
                    </span>
                  </button>
                ))}
                {filteredCountries.length === 0 && (
                  <div className="text-muted-foreground px-3 py-2 text-sm">
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <div className="flex-1">
          <input
            type="tel"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            className={`text-foreground placeholder-muted-foreground w-full rounded-r-md border-0 bg-transparent px-3 py-2 focus:ring-0 focus:outline-none ${getHeightClasses(size)} ${
              error ? "text-red-500" : ""
            } ${disabled ? "cursor-not-allowed" : ""}`}
          />
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
});

export { PhoneInputSimple };
